/**
 * Field alignment between `authoredSchema` keys and SED `FieldPath`s.
 *
 * Single source of truth for Populate prefill, Report fields resolution,
 * and schema-first completeness. Requirements that intentionally split
 * doc attestation from regional inference (e.g. geotech borehole vs
 * seismic) mark schema keys as `doc-only` and leave registry paths as
 * supporting-only.
 */
import type { ChecklistItem } from '../../config/documentChecklists';
import { DOC_CHECKLISTS, getDocumentChecklistFor } from '../../config/documentChecklists';
import type { ReportTypeId } from '../../config/reportTypes';
import { PROJECT_INTENT_ATTESTATION_DEFS } from '../projectIntentAttestations';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { FieldPath } from '../evidenceLineage/requirementToFields';
import {
  extractFieldValue,
  REQUIREMENT_TO_FIELDS,
  type ResolvedField,
} from '../evidenceLineage/requirementToFields';

export type FieldAlignmentKind = 'direct' | 'alias' | 'transform' | 'doc-only';

export interface RequirementFieldAlignment {
  schemaKey: string;
  fieldPath?: FieldPath;
  /** Additional SED paths tried in order when `fieldPath` is empty. */
  fallbackFieldPaths?: readonly FieldPath[];
  kind: FieldAlignmentKind;
  /** CAR transform id when kind === 'transform'. */
  transformId?: string;
}

type AlignmentRegistry = Record<string, readonly RequirementFieldAlignment[]>;

/** Per-requirement schema ↔ SED path links. */
export const REQUIREMENT_FIELD_ALIGNMENT: AlignmentRegistry = {
  geotech: [
    { schemaKey: 'bearingCapacity_kPa', fieldPath: 'geotechnical.professionalFacts.bearingCapacityKPa', kind: 'doc-only' },
    { schemaKey: 'soilClassification', fieldPath: 'geotechnical.professionalFacts.soilClassification', kind: 'doc-only' },
    { schemaKey: 'boreholeDepth_m', fieldPath: 'geotechnical.professionalFacts.boreholeDepthM', kind: 'doc-only' },
    { schemaKey: 'groundwaterDepth_m', fieldPath: 'geotechnical.professionalFacts.groundwaterDepthM', kind: 'doc-only' },
    { schemaKey: 'preparedBy', fieldPath: 'geotechnical.professionalFacts.preparedBy', kind: 'doc-only' },
    { schemaKey: 'reportDate', fieldPath: 'geotechnical.professionalFacts.reportDate', kind: 'doc-only' },
    { schemaKey: 'foundationRecommendation', fieldPath: 'geotechnical.professionalFacts.foundationRecommendation', kind: 'doc-only' },
    { schemaKey: 'settlementRisk', fieldPath: 'geotechnical.professionalFacts.settlementRisk', kind: 'doc-only' },
    { schemaKey: 'liquefactionRisk', fieldPath: 'geotechnical.professionalFacts.liquefactionRisk', kind: 'doc-only' },
    { schemaKey: 'expansiveSoilRisk', fieldPath: 'geotechnical.professionalFacts.expansiveSoilRisk', kind: 'doc-only' },
    { schemaKey: 'seismicSiteClass', fieldPath: 'geotechnical.professionalFacts.seismicSiteClass', kind: 'doc-only' },
  ],
  'utility-letter': [
    { schemaKey: 'utilityName', fieldPath: 'grid.utilityName', kind: 'direct' },
    { schemaKey: 'serviceDate', kind: 'doc-only' },
    { schemaKey: 'capacityOffered_MW', kind: 'transform', transformId: 'mapGridDataToCoverage' },
    { schemaKey: 'interconnectionClass', kind: 'transform', transformId: 'mapGridDataToCoverage' },
    { schemaKey: 'costEstimate_USD', kind: 'doc-only' },
    { schemaKey: 'nearestTransmissionKm', fieldPath: 'grid.nearestTransmissionKm', kind: 'alias' },
    { schemaKey: 'substationDistKm', fieldPath: 'grid.substationDistKm', kind: 'alias' },
    { schemaKey: 'nearestPipelineKm', fieldPath: 'onSiteInfrastructure.nearestPipelineKm', kind: 'alias' },
    { schemaKey: 'nearestPipelineOperator', fieldPath: 'onSiteInfrastructure.nearestPipelineOperator', kind: 'alias' },
    { schemaKey: 'nearestPipelineAvailabilityPct', fieldPath: 'onSiteInfrastructure.nearestPipelineAvailabilityPct', kind: 'alias' },
    { schemaKey: 'nearestPipelineMassFlowValue', fieldPath: 'onSiteInfrastructure.nearestPipelineMassFlowValue', kind: 'alias' },
    { schemaKey: 'nearestPipelineMassFlowUnit', fieldPath: 'onSiteInfrastructure.nearestPipelineMassFlowUnit', kind: 'alias' },
    { schemaKey: 'nearestPipelineMassFlowBasis', fieldPath: 'onSiteInfrastructure.nearestPipelineMassFlowBasis', kind: 'alias' },
  ],
  'parcel-survey': [
    { schemaKey: 'parcelId', kind: 'doc-only' },
    { schemaKey: 'netUsableAcres', kind: 'doc-only' },
    {
      schemaKey: 'recordedAcreage',
      fieldPath: 'parcel.acres',
      fallbackFieldPaths: ['siteProfile.acres'],
      kind: 'alias',
    },
    { schemaKey: 'deedRestrictions', kind: 'doc-only' },
    { schemaKey: 'easements', kind: 'doc-only' },
  ],
  'alta-survey': [
    { schemaKey: 'standardYear', fieldPath: 'survey.standardYear', kind: 'doc-only' },
    { schemaKey: 'surveyor', fieldPath: 'survey.surveyor', kind: 'doc-only' },
    { schemaKey: 'surveyDate', fieldPath: 'survey.surveyDate', kind: 'doc-only' },
    { schemaKey: 'surveyedAcreage', fieldPath: 'survey.surveyedAcreage', kind: 'doc-only' },
    { schemaKey: 'legalDescriptionConfirmed', fieldPath: 'survey.legalDescriptionConfirmed', kind: 'doc-only' },
    { schemaKey: 'accessConfirmed', fieldPath: 'survey.accessConfirmed', kind: 'doc-only' },
    { schemaKey: 'easements', fieldPath: 'survey.easements', kind: 'doc-only' },
    { schemaKey: 'rightsOfWay', fieldPath: 'survey.rightsOfWay', kind: 'doc-only' },
    { schemaKey: 'encroachments', fieldPath: 'survey.encroachments', kind: 'doc-only' },
    { schemaKey: 'utilitiesObserved', fieldPath: 'survey.utilitiesObserved', kind: 'doc-only' },
    { schemaKey: 'tableAItems', fieldPath: 'survey.tableAItems', kind: 'doc-only' },
    { schemaKey: 'floodZone', fieldPath: 'survey.floodZone', kind: 'doc-only' },
    { schemaKey: 'contoursIncluded', fieldPath: 'survey.contoursIncluded', kind: 'doc-only' },
    { schemaKey: 'contourInterval_ft', fieldPath: 'survey.contourIntervalFt', kind: 'doc-only' },
    { schemaKey: 'verticalDatum', fieldPath: 'survey.verticalDatum', kind: 'doc-only' },
  ],
  'mineral-title': [
    { schemaKey: 'preparer', fieldPath: 'mineralResources.titleFacts.preparer', kind: 'doc-only' },
    { schemaKey: 'reportDate', fieldPath: 'mineralResources.titleFacts.reportDate', kind: 'doc-only' },
    { schemaKey: 'countyRecordReference', fieldPath: 'mineralResources.titleFacts.countyRecordReference', kind: 'doc-only' },
    { schemaKey: 'ownershipStatus', fieldPath: 'mineralResources.titleFacts.ownershipStatus', kind: 'doc-only' },
    { schemaKey: 'reservedCommodities', fieldPath: 'mineralResources.titleFacts.reservedCommodities', kind: 'doc-only' },
    { schemaKey: 'activeLeases', fieldPath: 'mineralResources.titleFacts.activeLeases', kind: 'doc-only' },
    { schemaKey: 'surfaceUseRestrictions', fieldPath: 'mineralResources.titleFacts.surfaceUseRestrictions', kind: 'doc-only' },
  ],
  zoning: [
    { schemaKey: 'zoningCode', fieldPath: 'parcel.zoning', kind: 'alias' },
    {
      schemaKey: 'permittedUses',
      fieldPath: 'parcel.landUse',
      kind: 'alias',
    },
    { schemaKey: 'setbacks_ft', kind: 'transform', transformId: 'mapZoningDataToCoverage' },
    { schemaKey: 'heightLimit_ft', kind: 'transform', transformId: 'mapZoningDataToCoverage' },
    { schemaKey: 'specialConditions', kind: 'doc-only' },
  ],
  'flood-cert': [
    { schemaKey: 'bfe_ft', kind: 'doc-only' },
    { schemaKey: 'floodZone', fieldPath: 'flood.floodZone', kind: 'direct' },
    { schemaKey: 'datum', kind: 'doc-only' },
    { schemaKey: 'topOfLowestFloor_ft', kind: 'doc-only' },
  ],
  'phase1-esa': [
    { schemaKey: 'preparer', kind: 'doc-only' },
    { schemaKey: 'completedDate', kind: 'doc-only' },
    // A recognized environmental condition is not a habitat determination.
    { schemaKey: 'recRecognized', kind: 'doc-only' },
    { schemaKey: 'recDetails', kind: 'doc-only' },
  ],
  wetland: [
    { schemaKey: 'wetlandsPresent', fieldPath: 'environmental.hasWetlands', kind: 'alias' },
    { schemaKey: 'wetlandAcres', fieldPath: 'environmental.wetlandAcres', kind: 'alias' },
    { schemaKey: 'wetlandType', fieldPath: 'environmental.wetlandType', kind: 'alias' },
  ],
  'species-survey': [
    { schemaKey: 'speciesObserved', fieldPath: 'environmental.species', kind: 'alias' },
    { schemaKey: 'criticalHabitat', fieldPath: 'environmental.hasCriticalHabitat', kind: 'alias' },
    { schemaKey: 'surveyDate', kind: 'doc-only' },
  ],
  cultural: [
    { schemaKey: 'shpoStatus', kind: 'doc-only' },
    { schemaKey: 'consultationDate', kind: 'doc-only' },
    { schemaKey: 'historicProperties', kind: 'doc-only' },
    { schemaKey: 'tribalConsultation', kind: 'doc-only' },
  ],
  'archaeological-survey': [
    { schemaKey: 'qualifiedProfessional', fieldPath: 'culturalResources.professionalSurvey.qualifiedProfessional', kind: 'doc-only' },
    { schemaKey: 'surveyDate', fieldPath: 'culturalResources.professionalSurvey.surveyDate', kind: 'doc-only' },
    { schemaKey: 'surveyLevel', fieldPath: 'culturalResources.professionalSurvey.surveyLevel', kind: 'doc-only' },
    { schemaKey: 'acreageSurveyed', fieldPath: 'culturalResources.professionalSurvey.acreageSurveyed', kind: 'doc-only' },
    { schemaKey: 'resourcesIdentified', fieldPath: 'culturalResources.professionalSurvey.resourcesIdentified', kind: 'doc-only' },
    { schemaKey: 'resourceCount', fieldPath: 'culturalResources.professionalSurvey.resourceCount', kind: 'doc-only' },
    { schemaKey: 'eligibilityRecommendation', fieldPath: 'culturalResources.professionalSurvey.eligibilityRecommendation', kind: 'doc-only' },
    { schemaKey: 'effectFinding', fieldPath: 'culturalResources.professionalSurvey.effectFinding', kind: 'doc-only' },
    { schemaKey: 'shpoCaseNumber', fieldPath: 'culturalResources.professionalSurvey.shpoCaseNumber', kind: 'doc-only' },
  ],
  'iso-queue': [
    { schemaKey: 'iso', fieldPath: 'grid.isoRegion', kind: 'alias' },
    { schemaKey: 'queuePosition', kind: 'doc-only' },
    { schemaKey: 'requestedMW', kind: 'doc-only' },
    { schemaKey: 'expectedClusterStudy', fieldPath: 'grid.estimatedQueueTime', kind: 'alias' },
  ],
  tariff: [
    { schemaKey: 'retailRate_centsKWh', fieldPath: 'electricity.retailRate', kind: 'alias' },
    { schemaKey: 'contractTerm_years', kind: 'doc-only' },
    { schemaKey: 'priceEscalator_pct', kind: 'doc-only' },
  ],
  'water-rights': [
    { schemaKey: 'permittedVolume_MGD', kind: 'doc-only' },
    { schemaKey: 'waterSource', kind: 'doc-only' },
    { schemaKey: 'priorityDate', kind: 'doc-only' },
  ],
  'aquifer-study': [
    { schemaKey: 'aquiferName', kind: 'doc-only' },
    { schemaKey: 'staticWaterLevel_ft', kind: 'doc-only' },
    { schemaKey: 'estimatedYield_gpm', kind: 'doc-only' },
  ],
  'discharge-permit': [
    { schemaKey: 'permitNumber', kind: 'doc-only' },
    { schemaKey: 'permittedDischarge_MGD', kind: 'doc-only' },
    { schemaKey: 'receivingWater', kind: 'doc-only' },
  ],
  // P6 — every key rides one transform that assembles the whole claim into
  // TWO atomic single-segment patches (`waterResource.municipalSupply`,
  // `waterResource.wastewaterService`) — the section patch merger drops
  // nested paths and the authored overlay would mis-write them.
  'municipal-utility-letter': [
    { schemaKey: 'siteWaterAllocationMgd', kind: 'doc-only' },
    { schemaKey: 'projectAverageWaterDemandMgd', kind: 'doc-only' },
    { schemaKey: 'projectPeakWaterDemandMgd', kind: 'doc-only' },
    { schemaKey: 'projectWaterMarginMgd', kind: 'doc-only' },
    { schemaKey: 'siteWastewaterAllocationMgd', kind: 'doc-only' },
    { schemaKey: 'projectPeakWastewaterDischargeMgd', kind: 'doc-only' },
    { schemaKey: 'projectWastewaterMarginMgd', kind: 'doc-only' },
    { schemaKey: 'minimumWaterPressurePsi', kind: 'doc-only' },
    { schemaKey: 'waterServiceDate', kind: 'doc-only' },
    { schemaKey: 'supplyProvider', kind: 'transform', transformId: 'mapMunicipalUtilityToWaterResource' },
    { schemaKey: 'supplySourceDescription', kind: 'transform', transformId: 'mapMunicipalUtilityToWaterResource' },
    { schemaKey: 'supplyAvgMgd', kind: 'transform', transformId: 'mapMunicipalUtilityToWaterResource' },
    { schemaKey: 'supplyPeakMgd', kind: 'transform', transformId: 'mapMunicipalUtilityToWaterResource' },
    { schemaKey: 'supplyPermittedMgd', kind: 'transform', transformId: 'mapMunicipalUtilityToWaterResource' },
    { schemaKey: 'supplyExcessMgd', kind: 'transform', transformId: 'mapMunicipalUtilityToWaterResource' },
    { schemaKey: 'supplyStorageGallons', kind: 'transform', transformId: 'mapMunicipalUtilityToWaterResource' },
    { schemaKey: 'supplyNearestMainMiles', kind: 'transform', transformId: 'mapMunicipalUtilityToWaterResource' },
    { schemaKey: 'supplyLineToSite', kind: 'transform', transformId: 'mapMunicipalUtilityToWaterResource' },
    { schemaKey: 'wastewaterProvider', kind: 'transform', transformId: 'mapMunicipalUtilityToWaterResource' },
    { schemaKey: 'wastewaterPlantDescription', kind: 'transform', transformId: 'mapMunicipalUtilityToWaterResource' },
    { schemaKey: 'wastewaterPermittedMgd', kind: 'transform', transformId: 'mapMunicipalUtilityToWaterResource' },
    { schemaKey: 'wastewaterAvgMgd', kind: 'transform', transformId: 'mapMunicipalUtilityToWaterResource' },
    { schemaKey: 'wastewaterPeakMgd', kind: 'transform', transformId: 'mapMunicipalUtilityToWaterResource' },
    { schemaKey: 'wastewaterExcessMgd', kind: 'transform', transformId: 'mapMunicipalUtilityToWaterResource' },
    { schemaKey: 'wastewaterNearestConnectionMiles', kind: 'transform', transformId: 'mapMunicipalUtilityToWaterResource' },
    { schemaKey: 'wastewaterLineToSite', kind: 'transform', transformId: 'mapMunicipalUtilityToWaterResource' },
    { schemaKey: 'wastewaterConnectionType', kind: 'transform', transformId: 'mapMunicipalUtilityToWaterResource' },
    { schemaKey: 'statedBy', kind: 'transform', transformId: 'mapMunicipalUtilityToWaterResource' },
    { schemaKey: 'statedOn', kind: 'transform', transformId: 'mapMunicipalUtilityToWaterResource' },
    { schemaKey: 'appliesToProject', kind: 'transform', transformId: 'mapMunicipalUtilityToWaterResource' },
  ],
  survey: [
    { schemaKey: 'surveyor', kind: 'doc-only' },
    { schemaKey: 'surveyDate', kind: 'doc-only' },
    {
      schemaKey: 'surveyedAcreage',
      fieldPath: 'parcel.acres',
      fallbackFieldPaths: ['siteProfile.acres'],
      kind: 'alias',
    },
    { schemaKey: 'avgSlope_pct', kind: 'doc-only' },
    { schemaKey: 'verticalDatum', kind: 'doc-only' },
  ],
  'utility-locate': [
    { schemaKey: 'locator', kind: 'doc-only' },
    { schemaKey: 'locateDate', kind: 'doc-only' },
    { schemaKey: 'utilitiesLocated', kind: 'doc-only' },
    { schemaKey: 'encumbrances', kind: 'doc-only' },
    { schemaKey: 'setbackImpact_ft', kind: 'doc-only' },
  ],
  'fiber-survey': [
    { schemaKey: 'carrier', fieldPath: 'connectivity.fiberProvider', kind: 'alias' },
    { schemaKey: 'serviceType', kind: 'doc-only' },
    { schemaKey: 'capacity_Gbps', kind: 'doc-only' },
    { schemaKey: 'buildDistance_ft', fieldPath: 'connectivity.fiberBuildDistanceFt', kind: 'alias' },
    { schemaKey: 'quoteValidUntil', kind: 'doc-only' },
    { schemaKey: 'mrc_USD', kind: 'doc-only' },
  ],
  'latency-test': [
    { schemaKey: 'targetEndpoint', kind: 'doc-only' },
    { schemaKey: 'rtt_ms', kind: 'doc-only' },
    { schemaKey: 'jitter_ms', kind: 'doc-only' },
    { schemaKey: 'packetLoss_pct', kind: 'doc-only' },
    { schemaKey: 'testDate', kind: 'doc-only' },
    { schemaKey: 'testTool', kind: 'doc-only' },
  ],
  'community-feedback-summary': [
    { schemaKey: 'overallStance', kind: 'transform', transformId: 'mapPublicSentimentToCoverage' },
    { schemaKey: 'sentimentScore_0to100', kind: 'transform', transformId: 'mapPublicSentimentToCoverage' },
    { schemaKey: 'keyThemes', kind: 'transform', transformId: 'mapPublicSentimentToCoverage' },
    { schemaKey: 'stakeholderCount', kind: 'doc-only' },
    { schemaKey: 'lastEngagementDate', kind: 'doc-only' },
  ],
  'btm-vendor-quote': [
    { schemaKey: 'technology', kind: 'transform', transformId: 'mapBtmSedToCoverage' },
    { schemaKey: 'capacity_MW', kind: 'doc-only' },
    { schemaKey: 'lcoe_USDperMWh', kind: 'doc-only' },
    { schemaKey: 'leadTime_months', kind: 'doc-only' },
    { schemaKey: 'vendorName', kind: 'doc-only' },
    { schemaKey: 'ambientTemp_C', fieldPath: 'climate.avgTemp', kind: 'alias' },
    { schemaKey: 'wetBulb_C', fieldPath: 'climate.avgWetBulb', kind: 'alias' },
    { schemaKey: 'peakTemp_C', fieldPath: 'climate.peakTemp', kind: 'alias' },
    { schemaKey: 'humidity_pct', fieldPath: 'climate.humidity', kind: 'alias' },
    { schemaKey: 'waterStressLevel', fieldPath: 'waterStress.stressLevel', kind: 'alias' },
    { schemaKey: 'elevation_m', fieldPath: 'siteProfile.elevationAvg', kind: 'alias' },
    { schemaKey: 'nearestTransmissionKm', fieldPath: 'grid.nearestTransmissionKm', kind: 'alias' },
    { schemaKey: 'substationDistKm', fieldPath: 'grid.substationDistKm', kind: 'alias' },
    { schemaKey: 'nearestPipelineKm', fieldPath: 'onSiteInfrastructure.nearestPipelineKm', kind: 'alias' },
    { schemaKey: 'nearestPipelineOperator', fieldPath: 'onSiteInfrastructure.nearestPipelineOperator', kind: 'alias' },
    { schemaKey: 'nearestPipelineAvailabilityPct', fieldPath: 'onSiteInfrastructure.nearestPipelineAvailabilityPct', kind: 'alias' },
    { schemaKey: 'nearestPipelineMassFlowValue', fieldPath: 'onSiteInfrastructure.nearestPipelineMassFlowValue', kind: 'alias' },
    { schemaKey: 'nearestPipelineMassFlowUnit', fieldPath: 'onSiteInfrastructure.nearestPipelineMassFlowUnit', kind: 'alias' },
    { schemaKey: 'nearestPipelineMassFlowBasis', fieldPath: 'onSiteInfrastructure.nearestPipelineMassFlowBasis', kind: 'alias' },
  ],
  'vendor-quote-pack': [
    { schemaKey: 'supplierCount', kind: 'transform', transformId: 'mapSupplyChainPlanToCoverage' },
    { schemaKey: 'longestLeadMonths', kind: 'transform', transformId: 'mapSupplyChainPlanToCoverage' },
    { schemaKey: 'quoteCoverage_pct', kind: 'transform', transformId: 'mapSupplyChainPlanToCoverage' },
    { schemaKey: 'topRiskCategories', kind: 'transform', transformId: 'mapSupplyChainPlanToCoverage' },
  ],
  'diligence-source-pack': [],
  'prior-report-pack': [],
};

/** doc-only alignment rows for setup / intent schema keys. */
function setupFieldAlignments(keys: readonly string[]): RequirementFieldAlignment[] {
  return keys.map(schemaKey => ({
    schemaKey,
    kind: 'transform' as const,
    transformId: 'mapReportSetupToCoverage',
  }));
}

Object.assign(REQUIREMENT_FIELD_ALIGNMENT, {
  'site-project-intent': setupFieldAlignments([
    'powerRequirement',
    'powerRequirementMW',
    'workloadProfile',
    'coolingStrategy',
    // P0-7 — BTM cooling axes + expanded-profile rows now surfaced in the review.
    'bopHeatRejection',
    'coolingGeneration',
    'nuclearInterest',
    'priority',
    'inServiceDate',
    'powerBasis',
    'pueValue',
    'powerFactor',
    'annualDemandUtilizationRangePct.low',
    'annualDemandUtilizationRangePct.high',
    'datacenterTierTarget',
    'redundancyClass',
    'carbonCommitment',
    // P1 (field-architecture audit §3.6/§4) — SIX net-new BTM-feasibility intent
    // fields surfaced in the (now-ungated) review. Intent-only (no SED leaf),
    // so they map through the report-setup transform like the other intent rows.
    'interconnectionStatus.queueStatus',
    'interconnectionStatus.queueId',
    'interconnectionStatus.studyPhase',
    'fuelAccess.supply',
    'fuelAccess.distanceKm',
    'waterSource.source',
    'waterSource.rightsStatus',
    'offtake.structure',
    'offtake.tenorYears',
    'offtake.pricePerMwhUsd',
    'capacityCreditIntent.target',
    'capacityCreditIntent.targetMW',
    ...PROJECT_INTENT_ATTESTATION_DEFS.map(def => `attestations.${def.key}`),
  ]),
  'test-fit-report-setup': setupFieldAlignments([
    'targetFootprintAcres',
    'phasing',
    'phaseCount',
    'setbackOverrideFt',
    'parkingRatioPerEmployee',
    'stormwaterDetentionGal',
  ]),
  'environmental-report-setup': setupFieldAlignments([
    'expectedNepaPathway',
    'airBasinOverride',
    'dischargeWaterTarget',
    'speciesConcerns',
    'tribalLandProximityKnown',
    'federalNexus',
  ]),
  'power-availability-report-setup': setupFieldAlignments([
    'btmStrategy',
    'renewablesPreference',
    'curtailmentRiskTolerance',
    'maxSubstationDistanceMiAmber',
    'maxSubstationDistanceMiRed',
    'timeToPowerCriticalDate',
  ]),
  'water-resource-report-setup': setupFieldAlignments([
    // GAP 3 (field-architecture wiring) — the cold-end heat-rejection SINK is a
    // derived, intent-only fact (the water-withdrawal axis). Maps through the
    // report-setup transform like the other derived setup rows.
    'heatRejectionSink',
    'droughtScenario',
    'wastewaterTarget',
    'waterRightsKnown',
  ]),
  'interconnection-report-setup': setupFieldAlignments([
    'bandwidthGbps',
    'carrierDiversityMin',
    'latencyAwsMs',
    'latencyAzureMs',
    'latencyGcpMs',
    'ixPeeringRequired',
    'submarineCableProximityImportant',
  ]),
  // P0-2 — feasibilitySetup() BTM decision facts replace the 5 KPI-weight rows.
  'feasibility-report-setup': setupFieldAlignments([
    'btmStrategy',
    'firmCapacityBasisMW',
    'datacenterTierTarget',
    'redundancyClass',
    'carbonCommitment',
  ]),
  // P0-3 — supplyChainSetup() procurement facts replace the dead KPI-weight row.
  'supply-chain-risk-report-setup': setupFieldAlignments([
    'targetEnergizationDate',
    'firmCapacityBasisMW',
    'datacenterTierTarget',
    'redundancyClass',
  ]),
  'custom-brief-report-setup': setupFieldAlignments([
    'briefName',
    'objective',
    'questions',
    'contextNotes',
    'counterparties',
    'keyDates',
  ]),
  // FIN-1 (wf w7nuyquwd) — Financial Viability Report setup fields.
  'financial-report-setup': setupFieldAlignments([
    'hurdleRateOverride',
    'pinnedPathwayComparisonId',
    'includeTaxCredits',
    'includePpaAdjustment',
  ]),
  'executive-summary-report-setup': setupFieldAlignments([
    'sourceReportIds',
    'synthesisFocus',
  ]),
});

/** Legacy prefill map — derived from alignment entries with fieldPath. */
export function buildRequirementFieldToSlotMap(): Record<string, Record<string, string>> {
  const out: Record<string, Record<string, string>> = {};
  for (const [reqId, rows] of Object.entries(REQUIREMENT_FIELD_ALIGNMENT)) {
    const map: Record<string, string> = {};
    for (const row of rows) {
      if (row.fieldPath && row.kind !== 'transform') {
        map[row.schemaKey] = row.fieldPath;
      }
    }
    if (Object.keys(map).length > 0) out[reqId] = map;
  }
  return out;
}

export function getFieldAlignment(
  requirementId: string,
  schemaKey: string,
): RequirementFieldAlignment | undefined {
  return (REQUIREMENT_FIELD_ALIGNMENT[requirementId] ?? []).find(a => a.schemaKey === schemaKey);
}

/** Ordered SED paths to try for a schema key (primary then fallbacks). */
export function getAlignedFieldPaths(
  requirementId: string,
  schemaKey: string,
): FieldPath[] {
  const alignment = getFieldAlignment(requirementId, schemaKey);
  if (!alignment?.fieldPath) return [];
  const paths: FieldPath[] = [alignment.fieldPath];
  for (const p of alignment.fallbackFieldPaths ?? []) {
    if (!paths.includes(p)) paths.push(p);
  }
  return paths;
}

/**
 * Resolve the first populated SED value for an aligned schema key.
 * Used by Report fields + Populate prefill so KMZ/site-profile values
 * (e.g. `siteProfile.acres`) surface in authored schema slots.
 */
export function resolveAlignedSedField(
  sed: SiteEvidenceDocument | undefined,
  requirementId: string,
  schemaKey: string,
): ResolvedField | undefined {
  if (!sed) return undefined;
  for (const path of getAlignedFieldPaths(requirementId, schemaKey)) {
    const resolved = extractFieldValue(sed, path);
    if (!resolved.isEmpty) return resolved;
  }
  return undefined;
}

export function getAlignmentForRequirement(
  requirementId: string,
): readonly RequirementFieldAlignment[] {
  return REQUIREMENT_FIELD_ALIGNMENT[requirementId] ?? [];
}

/** SED paths linked to schema attestation (direct / alias). */
export function getAttestationFieldPaths(requirementId: string): FieldPath[] {
  return (REQUIREMENT_FIELD_ALIGNMENT[requirementId] ?? [])
    .filter(a => a.fieldPath && a.kind !== 'doc-only' && a.kind !== 'transform')
    .map(a => a.fieldPath!);
}

/** Registry paths linked to schema attestation (direct / alias / transform targets). */
export function getLinkedFieldPaths(requirementId: string): FieldPath[] {
  const linked = new Set<FieldPath>();
  for (const row of REQUIREMENT_FIELD_ALIGNMENT[requirementId] ?? []) {
    if (row.fieldPath) linked.add(row.fieldPath);
    for (const p of row.fallbackFieldPaths ?? []) linked.add(p);
  }
  return [...linked];
}

/**
 * Merge alignment attestation paths (first) with legacy supporting paths.
 * Ensures schema-linked SED fields precede provider-only context fields.
 */
export function mergeWithLegacyFieldPaths(
  requirementId: string,
  legacyPaths: readonly FieldPath[],
): FieldPath[] {
  const attestation = getAttestationFieldPaths(requirementId);
  const linked = getLinkedFieldPaths(requirementId);
  const primary = [...new Set([...attestation, ...linked])];
  const supporting = legacyPaths.filter(p => !primary.includes(p));
  return [...primary, ...supporting];
}

/** Registry paths not linked to any schema key — supporting site data only. */
export function getSupportingOnlyFieldPaths(requirementId: string): FieldPath[] {
  const linked = new Set(getLinkedFieldPaths(requirementId));
  return (REQUIREMENT_TO_FIELDS[requirementId] ?? []).filter(p => !linked.has(p));
}

/** All checklist items that declare authoredSchema (deduped by id). */
export function allChecklistItemsWithSchema(): ChecklistItem[] {
  const seen = new Set<string>();
  const out: ChecklistItem[] = [];
  const typeIds = Object.keys(DOC_CHECKLISTS) as ReportTypeId[];
  for (const typeId of typeIds) {
    for (const item of getDocumentChecklistFor(typeId)) {
      if (seen.has(item.id)) continue;
      if ((item.authoredSchema ?? []).some(f => f.key !== 'notes')) {
        seen.add(item.id);
        out.push(item);
      }
    }
  }
  return out;
}
